/**
 * License-related types for arXiv paper license checking and validation.
 * Based on Creative Commons license types and arXiv-specific license information.
 */

/**
 * Types of license restrictions that can apply to papers
 */
export type LicenseRestrictionType = 'no-derivatives' | 'non-commercial' | 'share-alike';

/**
 * License restriction details
 */
export interface LicenseRestriction {
  type: LicenseRestrictionType;
  description: string;
}

/**
 * arXiv license information extracted from OAI-PMH metadata
 */
export interface ArxivLicense {
  /** Unique identifier for the license type */
  id: string;
  /** Human-readable license name */
  name: string;
  /** URL to full license text (optional) */
  url?: string;
  /** List of restrictions imposed by this license */
  restrictions: LicenseRestriction[];
  /** Whether this license is compliant with platform policies */
  isCompliant: boolean;
  /** Raw license text from arXiv metadata (for debugging) */
  rawText?: string;
}

/**
 * Result of license fetching operation
 */
export interface LicenseFetchResult {
  /** Whether the fetch operation was successful */
  success: boolean;
  /** License information if successful */
  license?: ArxivLicense;
  /** Error message if unsuccessful */
  error?: string;
  /** arXiv ID that was queried */
  arxivId: string;
}

/**
 * License validation result
 */
export interface LicenseValidationResult {
  /** Whether the license is valid and compliant */
  isValid: boolean;
  /** Whether the license is compliant with platform policies */
  isCompliant: boolean;
  /** License information if valid */
  license?: ArxivLicense;
  /** Validation error messages */
  errors: string[];
}

/**
 * License compliance status for database storage
 */
export type LicenseComplianceStatus = 'compliant' | 'non_compliant' | 'pending' | 'unknown';

/**
 * OAI-PMH request parameters for license fetching
 */
export interface OaiPmhRequest {
  /** OAI verb (always 'GetRecord' for individual papers) */
  verb: 'GetRecord';
  /** Paper identifier in OAI format */
  identifier: string;
  /** Metadata format (use 'arXiv' for license information) */
  metadataPrefix: 'arXiv';
}

/**
 * Common Creative Commons license identifiers
 */
export const CC_LICENSE_IDS = {
  CC_BY: 'cc-by',
  CC_BY_SA: 'cc-by-sa',
  CC_BY_NC: 'cc-by-nc',
  CC_BY_NC_SA: 'cc-by-nc-sa',
  CC_BY_ND: 'cc-by-nd',
  CC_BY_NC_ND: 'cc-by-nc-nd', // Non-compliant
  CC_ZERO: 'cc0',
  ARXIV_DEFAULT: 'arxiv-default'
} as const;

/**
 * Non-compliant license patterns (case-insensitive)
 */
export const NON_COMPLIANT_PATTERNS = [
  'CC BY-NC-ND',
  'by-nc-nd',
  'Attribution-NonCommercial-NoDerivatives',
  'noncommercial-noderivatives'
] as const;

/**
 * Default arXiv license for papers without explicit Creative Commons licensing
 */
export const DEFAULT_ARXIV_LICENSE: ArxivLicense = {
  id: CC_LICENSE_IDS.ARXIV_DEFAULT,
  name: 'arXiv.org perpetual, non-exclusive license',
  url: 'https://arxiv.org/licenses/nonexclusive-distrib/1.0/',
  restrictions: [],
  isCompliant: true
};

/**
 * Rate limiting configuration for arXiv OAI-PMH API
 */
export const ARXIV_RATE_LIMIT_MS = 3000; // 3 seconds as required by arXiv ToS

/**
 * Type guard to check if a license is non-compliant
 */
export function isNonCompliantLicense(license: ArxivLicense): boolean {
  return NON_COMPLIANT_PATTERNS.some(pattern =>
    license.name.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Type guard to validate ArxivLicense object
 */
export function isValidArxivLicense(obj: unknown): obj is ArxivLicense {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as ArxivLicense).id === 'string' &&
    typeof (obj as ArxivLicense).name === 'string' &&
    typeof (obj as ArxivLicense).isCompliant === 'boolean' &&
    Array.isArray((obj as ArxivLicense).restrictions) &&
    (obj as ArxivLicense).restrictions.every((r: LicenseRestriction) =>
      typeof r.type === 'string' &&
      typeof r.description === 'string'
    )
  );
}

/**
 * Error class for license-related operations
 */
export class LicenseError extends Error {
  constructor(
    message: string,
    public readonly arxivId?: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'LicenseError';
  }
}

/**
 * Specific error for license fetch failures
 */
export class LicenseFetchError extends LicenseError {
  constructor(message: string, arxivId?: string, cause?: Error) {
    super(message, arxivId, cause);
    this.name = 'LicenseFetchError';
  }
}

/**
 * Specific error for license validation failures
 */
export class LicenseValidationError extends LicenseError {
  constructor(message: string, arxivId?: string, cause?: Error) {
    super(message, arxivId, cause);
    this.name = 'LicenseValidationError';
  }
}